import { Box, styled, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BaseModal } from 'ui/BaseModal';
import { ButtonV2 } from 'ui/ButtonV2';
import HomeIcon from '@mui/icons-material/Home';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationPinIcon from '@mui/icons-material/LocationPin';
import { IAddress } from '@pepperhq/types-sdk';
import { addOpacity } from 'lib/helpers';
import moment from 'moment';

interface DeliveryConfirmDialogProps {
    open: boolean;
    onCancel: () => void;
    onSubmit?: () => void;
    dropoff?: Date;
    dropoffDeadline?: Date;
    to?: IAddress['address'];
    from?: string;
}

const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
    backgroundColor: addOpacity(theme.palette.text.primary, 0.05)
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    marginTop: theme.spacing(1),
    fontFamily: `TenantFont, ${theme.typography.fontFamily}`
}));

const StyledSubTitle = styled(Typography)(({ theme }) => ({
    fontFamily: `TenantFont, ${theme.typography.fontFamily}`,
    opacity: 0.7
}));

const StyledIconBox = styled(Box)(({ theme }) => ({
    margin: theme.spacing(0, 2, 0, 0)
}));

export const DeliveryConfirmDialog: React.FC<DeliveryConfirmDialogProps> = ({
    open,
    onCancel,
    onSubmit,
    dropoff,
    dropoffDeadline,
    to,
    from
}) => {
    const { t } = useTranslation();
    return (
        <BaseModal open={open} onClose={onCancel}>
            <StyledTitle variant="h6" align="center" sx={{ fontWeight: 700, mt: 1 }}>
                {t('DELIVERY_CONFIRMATION_DIALOG_TITLE')}
            </StyledTitle>
            <Box px={1} pt={1}>
                <StyledBox>
                    <StyledIconBox>
                        <LocationPinIcon sx={{ fontSize: '2rem' }} />
                    </StyledIconBox>
                    <Box>
                        <StyledSubTitle>{t('DELIVERY_CONFIRMATION_DIALOG_FROM')}</StyledSubTitle>
                        <Typography>{from}</Typography>
                    </Box>
                </StyledBox>
                <StyledBox>
                    <StyledIconBox>
                        <HomeIcon sx={{ fontSize: '2rem' }} />
                    </StyledIconBox>
                    <Box>
                        <StyledSubTitle>{t('DELIVERY_CONFIRMATION_DIALOG_TO')}</StyledSubTitle>
                        <Typography>{to?.line1}</Typography>
                        {to?.line2 && <Typography>{to.line2}</Typography>}
                        <Typography>
                            {to?.city}
                            {to?.region ? `, ${to.region}` : ''}
                        </Typography>
                        <Typography>{to?.postalCode}</Typography>
                    </Box>
                </StyledBox>
                <StyledBox>
                    <StyledIconBox>
                        <AccessTimeIcon sx={{ fontSize: '2rem' }} />
                    </StyledIconBox>
                    <Box>
                        <StyledSubTitle>{t('DELIVERY_CONFIRMATION_DIALOG_WHEN')}</StyledSubTitle>
                        <Typography>
                            {t('DELIVERY_ETA_TIMESLOT_MESSAGE', {
                                timeslot: `${moment(dropoff).format('hh:mm a')} - ${moment(
                                    dropoffDeadline
                                ).format('hh:mm a')}`
                            })}
                        </Typography>
                    </Box>
                </StyledBox>
            </Box>
            <ButtonV2 onClick={onSubmit}>{t('DELIVERY_CONFIRMATION_DIALOG_SUBMIT')}</ButtonV2>
            <ButtonV2 onClick={onCancel} variant="outlined">
                {t('GENERAL_CANCEL')}
            </ButtonV2>
        </BaseModal>
    );
};
